import React from 'react';
import {
  Flex,
  Box,
  Heading,
  Text,
  LoadingOrbit,
  Card,
  CardContent,
  CardHeader,
  Switch,
  Button,
  Select,
  UnderlineTabs,
  UnderlineTab,
  type SelectOption,
} from '@chatsdet/ui';
import { Check, X, PaperPlaneTilt } from '@phosphor-icons/react';
import {
  useUserEventSlackSettingsQuery,
  useToggleUserEventSlackMutation,
  useUserEventChannelSettingsQuery,
  useUserEventChannelListQuery,
  useUpdateUserEventChannelMutation,
  useTestUserEventChannelMutation,
} from './useUserEventData';
import { USER_EVENT_TYPES } from '@chatsdet/user-events';
import { UserEventsCanvas } from './userEventsChat/UserEventsCanvas';
import { UserEventsChatPanel } from './userEventsChat/UserEventsChatPanel';
import { useUserEventsChat } from './userEventsChat/hooks/useUserEventsChat';
import { DashboardsList } from './userEventsChat/dashboards/DashboardsList';
import { DashboardDetail } from './userEventsChat/dashboards/DashboardDetail';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { superadminUserEventsRoute } from '../../../router';

// --- Explore Tab (chat + canvas) ---

function ExploreTab() {
  const {
    messages,
    chatInput,
    setChatInput,
    isSending,
    isEnabled,
    handleSendMessage,
    handleStopChat,
  } = useUserEventsChat();

  return (
    <div className="flex flex-1 min-h-0">
      <div className="flex-1 min-w-0">
        <UserEventsCanvas messages={messages} />
      </div>
      <div className="w-[380px] border-l border-border flex-shrink-0">
        <UserEventsChatPanel
          messages={messages}
          composerValue={chatInput}
          onComposerChange={setChatInput}
          onSend={handleSendMessage}
          onStop={handleStopChat}
          isSending={isSending}
          isEnabled={isEnabled}
        />
      </div>
    </div>
  );
}

// --- Slack Settings Section ---

function SlackSettingsSection() {
  const settingsQuery = useUserEventSlackSettingsQuery();
  const toggleMutation = useToggleUserEventSlackMutation();

  const settingsMap = React.useMemo(() => {
    const map = new Map<string, boolean>();
    for (const s of settingsQuery.data?.data ?? []) {
      map.set(s.eventType, s.slackEnabled);
    }
    return map;
  }, [settingsQuery.data]);

  return (
    <Box className="space-y-4">
      <Heading level={3}>Slack Notification Settings</Heading>
      <Card>
        <CardContent>
          {settingsQuery.isLoading ? (
            <LoadingOrbit />
          ) : (
            <Flex direction="column" gap="md">
              {USER_EVENT_TYPES.map((eventType) => {
                const enabled = settingsMap.get(eventType) ?? true;
                return (
                  <Flex key={eventType} justify="between" align="center" className="py-1">
                    <Text className="font-mono text-sm">{eventType}</Text>
                    <Switch
                      isSelected={enabled}
                      onChange={(val) =>
                        toggleMutation.mutate({ eventType, slackEnabled: val })
                      }
                    />
                  </Flex>
                );
              })}
            </Flex>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

// --- Slack Channel Config Section ---

function SlackChannelSection() {
  const settingsQuery = useUserEventChannelSettingsQuery();
  const channelsQuery = useUserEventChannelListQuery(
    !!settingsQuery.data?.data.connected
  );
  const updateMutation = useUpdateUserEventChannelMutation();
  const testMutation = useTestUserEventChannelMutation();

  const settings = settingsQuery.data?.data;
  const channels = channelsQuery.data?.data ?? [];
  const [selectedChannelId, setSelectedChannelId] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (settings) setSelectedChannelId(settings.channelId);
  }, [settings]);

  const handleChannelChange = async (channelId: string) => {
    setSelectedChannelId(channelId);
    const channel = channels.find((ch) => ch.id === channelId);
    await updateMutation.mutateAsync({
      channelId,
      channelName: channel?.name ?? '',
    });
  };

  const isConfigured = !!settings?.connected && !!settings?.channelId;

  return (
    <Box className="space-y-4">
      <Heading level={3}>Slack Channel</Heading>
      <Card>
        <CardHeader>
          <Text size="sm" className="text-muted-foreground">
            Channel used for all user-event Slack notifications. Respects the
            <code className="mx-1 font-mono text-xs">customer-alerts.slack-channel-id</code>
            knob.
          </Text>
        </CardHeader>
        <CardContent>
          {settingsQuery.isLoading ? (
            <LoadingOrbit size="sm" />
          ) : (
            <Flex direction="column" gap="md">
              <Flex direction="column" gap="xs">
                <Flex align="center" gap="sm">
                  {settings?.internalOrgId ? (
                    <Check size={14} className="text-green-600" />
                  ) : (
                    <X size={14} className="text-muted-foreground" />
                  )}
                  <Text size="sm">
                    Internal Org:{' '}
                    {settings?.internalOrgName ??
                      settings?.internalOrgId ??
                      'Not configured (set INTERNAL_ORG_ID)'}
                  </Text>
                </Flex>
                <Flex align="center" gap="sm">
                  {settings?.connected ? (
                    <Check size={14} className="text-green-600" />
                  ) : (
                    <X size={14} className="text-muted-foreground" />
                  )}
                  <Text size="sm">
                    Slack: {settings?.connected ? 'Connected' : 'Not connected'}
                  </Text>
                </Flex>
                <Flex align="center" gap="sm">
                  {settings?.channelId ? (
                    <Check size={14} className="text-green-600" />
                  ) : (
                    <X size={14} className="text-muted-foreground" />
                  )}
                  <Text size="sm">
                    Channel:{' '}
                    {settings?.channelName
                      ? `#${settings.channelName}`
                      : settings?.channelId ?? 'Not set'}
                  </Text>
                </Flex>
              </Flex>

              {settings?.connected && (
                <Box className="max-w-md">
                  {channelsQuery.isLoading ? (
                    <LoadingOrbit size="sm" />
                  ) : channelsQuery.isError ? (
                    <Text size="sm" className="text-destructive">
                      {channelsQuery.error instanceof Error
                        ? channelsQuery.error.message
                        : 'Failed to load channels'}
                    </Text>
                  ) : channels.length === 0 ? (
                    <Text size="sm" className="text-muted-foreground">
                      No channels available. Add the Slack bot to channels first.
                    </Text>
                  ) : (
                    <Select
                      items={
                        channels.map((ch) => ({
                          id: ch.id,
                          label: `#${ch.name}`,
                        })) satisfies SelectOption[]
                      }
                      selectedKey={selectedChannelId ?? undefined}
                      onSelectionChange={(value) => handleChannelChange(value as string)}
                      isDisabled={updateMutation.isPending}
                      placeholder="Select a channel..."
                    />
                  )}
                </Box>
              )}

              <Flex align="center" gap="sm">
                <Button
                  variant="outline"
                  onPress={() => testMutation.mutate()}
                  isDisabled={testMutation.isPending || !isConfigured}
                >
                  <PaperPlaneTilt size={16} />
                  {testMutation.isPending ? 'Sending…' : 'Send test message'}
                </Button>
                {testMutation.isSuccess && (
                  <Text size="sm" className="text-green-600">
                    Sent — check the channel.
                  </Text>
                )}
                {testMutation.isError && (
                  <Text size="sm" className="text-destructive">
                    {testMutation.error instanceof Error
                      ? testMutation.error.message
                      : 'Failed to send test'}
                  </Text>
                )}
              </Flex>
            </Flex>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

// --- Main Page ---

type TabId = 'explore' | 'dashboards' | 'slack';

export function SuperadminUserEvents() {
  const search = useSearch({ from: superadminUserEventsRoute.id });
  const navigate = useNavigate({ from: superadminUserEventsRoute.id });
  const activeTab = (search.tab ?? 'explore') as TabId;
  const dashboardId = search.dashboard;

  type SearchState = { tab: TabId; dashboard: string | undefined };

  const setTab = (tab: TabId) => {
    navigate({
      search: (prev: SearchState) => ({
        ...prev,
        tab,
        dashboard: tab === 'dashboards' ? prev.dashboard : undefined,
      }),
      replace: true,
    });
  };

  const openDashboard = (id: string) => {
    navigate({
      search: (prev: SearchState) => ({ ...prev, tab: 'dashboards', dashboard: id }),
    });
  };

  const closeDashboard = () => {
    navigate({
      search: (prev: SearchState) => ({ ...prev, tab: 'dashboards', dashboard: undefined }),
    });
  };

  return (
    <div className="-m-6 flex flex-col h-[calc(100vh-4rem)]">
      <UnderlineTabs
        selectedId={activeTab}
        onSelectionChange={(id) => setTab(id as TabId)}
        className="px-6 pt-2 flex-shrink-0"
      >
        <UnderlineTab id="explore" className="px-4 py-4 text-base">
          Explore
        </UnderlineTab>
        <UnderlineTab id="dashboards" className="px-4 py-4 text-base">
          Dashboards
        </UnderlineTab>
        <UnderlineTab id="slack" className="px-4 py-4 text-base">
          Slack
        </UnderlineTab>
      </UnderlineTabs>

      {activeTab === 'explore' ? (
        <ExploreTab />
      ) : activeTab === 'dashboards' ? (
        dashboardId ? (
          <DashboardDetail dashboardId={dashboardId} onBack={closeDashboard} />
        ) : (
          <DashboardsList onOpen={openDashboard} />
        )
      ) : (
        <Box className="space-y-8 p-6 overflow-y-auto">
          <SlackChannelSection />
          <SlackSettingsSection />
        </Box>
      )}
    </div>
  );
}
